import { StyleSheet } from 'aphrodite';
import {
	brandPrimaryHover,
	brandPrimaryText,
	brandSecondaryHover,
	connectionStatusText,
	darkGrayFontColor,
	destructive,
	header,
	label,
	midGray,
	navigation,
	success,
	titles,
	warningDark,
	white,
} from '../../../styles/colors';
import { THEME } from '../../../components-v2/Theme/theme';
import { baseStyleSheet, BrowserMediaQueries, CssSelectors, Layouts, truncateTextStyle } from '../../../styles/styles';

const colSpacing = 10;
const borderBottom = `1px ${midGray} solid`;

// Temporarily hide the per-page engagement metrics column while upstream metric
// retrieval is being stabilized. Flip back to `true` to re-enable it.
export const SHOW_ENGAGEMENT_COLUMN = false;

export const styleSheet = StyleSheet.create({
	alertContainer: {
		alignItems: 'center',
		display: 'flex',
		marginTop: 4,
	},
	alertMessage: {
		color: destructive,
		paddingLeft: 2,
	},
	body: {
		minHeight: 100,
		position: 'relative',
	},
	checkmarkIconRow: {
		alignItems: 'center',
		display: 'flex',
		height: 28,
		...truncateTextStyle,
	},
	clearFiltersContainer: {
		alignItems: 'flex-end',
		display: 'flex',
		height: 40,
		...CssSelectors.allChildren(
			{
				color: header,
			},
			'button'
		),
	},
	connectionRow: {
		alignItems: 'center',
		display: 'flex',
		paddingLeft: 10,
	},
	connectionStatus: {
		alignItems: 'center',
		color: connectionStatusText,
		display: 'flex',
		fontSize: 16,
		marginLeft: 4,
	},
	dateText: {
		color: darkGrayFontColor,
	},
	detailed: {
		color: brandPrimaryHover,
		fontSize: 12,
		marginTop: 4,
		width: 120,
	},
	filter: {
		alignItems: 'center',
		color: header,
		display: 'flex',
		fontSize: 14,
		justifyContent: 'flex-end',
		marginBottom: 20,
		marginTop: 10,
		width: '100%',
		gap: 8,
	},
	filterDropdownMenu: {
		fontSize: 14,
	},
	filterDropdownTrigger: {
		background: white,
		color: header,
		fontSize: 14,
		width: 180,
	},
	header: {
		...Layouts.horizontalStack(colSpacing),
		alignItems: 'center',
		borderBottom,
		boxSizing: 'border-box',
		display: 'flex',
		maxWidth: '100%',
		paddingBottom: 6,
	},
	icon: {
		marginRight: 4,
	},
	loading: {
		marginTop: 10,
	},
	nameColumn: {
		flex: 2.5,
	},
	nameColumnRow: {
		alignItems: 'center',
		color: brandPrimaryText,
		display: 'flex',
	},
	createdColumn: {
		flex: 1.5,
		color: darkGrayFontColor,
		fontSize: 14,
	},
	engagementColumn: {
		// Narrower than the platforms column so the stats stay compact and sit closer
		// to the "View Post On Platform" column; the freed width goes to `platformColumn`.
		flex: 2,
	},
	engagementStack: {
		display: 'flex',
		flexDirection: 'column',
	},
	engagementRow: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.sm,
		gap: THEME.spacing.sm,
		// Matches `platformIconRow` minHeight so each engagement row aligns
		// vertically with its corresponding page in the platforms column.
		minHeight: 28,
	},
	engagementStat: {
		alignItems: 'center',
		display: 'flex',
		flex: '0 0 auto',
		gap: THEME.spacing.xs,
		// Fixed slot width so the three stat columns line up across rows;
		// sized for icon + 4-digit values like "1,234".
		width: 68,
	},
	engagementValue: {
		fontVariantNumeric: 'tabular-nums',
		fontWeight: THEME.typography.fontWeights.bold,
		// Reserves room for "9,999" so the icon position never shifts.
		minWidth: 40,
		textAlign: 'left',
	},
	engagementIconReactions: {
		color: THEME.colors.danger.main,
		display: 'inline-flex',
		...CssSelectors.allDescendants({ fill: 'currentColor' }, 'path'),
	},
	engagementIconComments: {
		color: THEME.colors.hover.main,
		display: 'inline-flex',
		...CssSelectors.allDescendants({ stroke: 'currentColor' }, 'path'),
	},
	engagementIconShares: {
		// ShareIcon's path defaults to `fill='currentColor'`, so the wrapper's color
		// cascades into the SVG without a descendant rule like the other two icons need.
		color: THEME.colors.success.main,
		display: 'inline-flex',
	},
	engagementEmpty: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
		fontStyle: 'italic',
	},
	noneFound: {
		color: navigation,
		letterSpacing: 1.1,
		marginTop: 50,
		textAlign: 'center',
		textTransform: 'uppercase',
	},
	platformAccounts: {
		display: 'flex',
		flexDirection: 'column',
	},
	platformColumn: {
		// Wider than before (absorbs width freed up by the slimmer engagement column)
		// so page names wrap less often.
		flex: 4,
	},
	viewPostColumn: {
		flex: 2,
	},
	platformIconRow: {
		alignItems: 'center',
		display: 'flex',
		minHeight: 28,
		...CssSelectors.allChildren(
			{
				height: 16,
				marginRight: 2,
			},
			'img'
		),
	},
	platformText: {
		fontSize: 16,
	},
	popover: {
		display: 'inline-flex',
		margin: 0,
	},
	popoverWarning: {
		borderBottomRightRadius: 0,
		boxSizing: 'border-box',
		color: label,
		fontSize: 12,
		lineHeight: '18px',
		maxWidth: 180,
		padding: '6px 6px 6px 6px',
	},
	popoverAnchor: {
		marginLeft: 4,
	},
	popoverContent: {
		borderRadius: 3,
		color: titles,
		fontSize: 12,
		padding: '2px 6px',
		width: 210,
	},
	popoverMargin: {
		alignItems: 'center',
		display: 'flex',
	},
	reconnect: {
		color: destructive,
		marginLeft: -4,
	},
	reconnectButton: {
		flexGrow: 1,
		marginTop: 4,
		maxWidth: 200,
		paddingBottom: 2,
		paddingLeft: 4,
		paddingRight: 4,
		paddingTop: 2,
		[BrowserMediaQueries.IE11]: {
			flexGrow: 0,
		},
	},
	reconnectColumn: {
		width: '10%',
	},
	row: {
		alignItems: 'flex-start',
		borderBottom,
		boxSizing: 'border-box',
		display: 'flex',
		maxWidth: '100%',
		padding: '20px 0',
	},
	rowClipboardParent: {
		cursor: 'pointer',
		display: 'flex',
	},
	rowConnection: {
		alignItems: 'center',
		display: 'flex',
		paddingLeft: 10,
	},
	rowDetails: {
		color: navigation,
		fontSize: 11,
	},
	rowDetailsRow: {
		alignItems: 'center',
		display: 'flex',
	},
	rowDetailsStatus: {
		...baseStyleSheet.fontBold,
		textTransform: 'uppercase',
	},
	rowName: {
		width: '100%',
		...Layouts.verticalStack(8),
		...CssSelectors.allChildren(
			{
				fontWeight: 600,
				width: '100%',
			},
			'button'
		),
	},
	rowResponses: {
		alignItems: 'center',
		display: 'flex',
		paddingLeft: 10,
	},
	rowTitle: {
		alignItems: 'center',
		display: 'flex',
		gap: '0.5rem',
	},
	statusCancelled: {
		color: destructive,
	},
	statusFailed: {
		color: destructive,
	},
	statusPending: {
		color: warningDark,
	},
	statusScheduled: {
		color: brandSecondaryHover,
	},
	statusSucceeded: {
		color: success,
	},
	targetRows: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		width: 180,
	},
});
